// Operator "Roles & access" roster (COL-352 / Neel feedback #8a).
//
// The staff AdminRoleTierOverview groups by the platform-wide app_role, which
// is "operator" for every member of an org — so it can't show an operator its
// team split into Super Admins vs Admins. That split is the WITHIN-ORG
// member_role on operator_members, which is what this component reads.
//
// Isolation: operator_members RLS ("Members and staff view operator members")
// scopes the roster to the caller's own org, and operator_person_read already
// lets an operator read their co-members' profiles — so a bare select returns
// exactly this org's team, names included, and nothing else.
import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useOperator } from "@/hooks/useOperator";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Crown, Briefcase, Loader2, Users } from "lucide-react";

type MemberRole = "super_admin" | "admin";
interface TeamMember {
  user_id: string;
  member_role: MemberRole;
  name: string | null;
  email: string | null;
}

const TIERS: {
  role: MemberRole;
  label: string;
  blurb: string;
  icon: typeof Crown;
  tint: string;
}[] = [
  {
    role: "super_admin",
    label: "Super Admins",
    blurb:
      "Full access to your organization, and the only tier that can invite teammates.",
    icon: Crown,
    tint: "bg-amber-100 text-amber-700 dark:bg-amber-950/50 dark:text-amber-300",
  },
  {
    role: "admin",
    label: "Admins",
    blurb: "Day-to-day operations across your properties, tenants and owners.",
    icon: Briefcase,
    tint: "bg-teal-100 text-teal-700 dark:bg-teal-950/50 dark:text-teal-300",
  },
];

export function OperatorTeamRoster() {
  const { operator } = useOperator();

  const { data: members = [], isLoading } = useQuery({
    queryKey: ["operator-team-roster", operator?.id],
    enabled: !!operator?.id,
    queryFn: async (): Promise<TeamMember[]> => {
      // RLS scopes both reads to the caller's own org.
      const { data: rows, error } = await supabase
        .from("operator_members")
        .select("user_id, member_role");
      if (error) throw error;
      const memberRows = (rows ?? []) as {
        user_id: string;
        member_role: MemberRole;
      }[];
      if (memberRows.length === 0) return [];

      const ids = memberRows.map((r) => r.user_id);
      const { data: profileRows, error: pErr } = await supabase
        .from("profiles")
        .select("id, name, email")
        .in("id", ids);
      if (pErr) throw pErr;
      const byId = new Map(
        (profileRows ?? []).map(
          (p: { id: string; name: string | null; email: string | null }) => [
            p.id,
            p,
          ]
        )
      );
      return memberRows.map((r) => ({
        user_id: r.user_id,
        member_role: r.member_role,
        name: byId.get(r.user_id)?.name ?? null,
        email: byId.get(r.user_id)?.email ?? null,
      }));
    },
  });

  const grouped = useMemo(() => {
    const g: Record<MemberRole, TeamMember[]> = { super_admin: [], admin: [] };
    for (const m of members) {
      if (m.member_role === "super_admin" || m.member_role === "admin")
        g[m.member_role].push(m);
    }
    return g;
  }, [members]);

  if (isLoading) {
    return (
      <div className="flex min-h-[20vh] items-center justify-center">
        <Loader2
          className="h-6 w-6 animate-spin text-muted-foreground"
          aria-label="Loading team"
        />
      </div>
    );
  }

  return (
    <div className="grid gap-4 md:grid-cols-2">
      {TIERS.map((tier) => {
        const Icon = tier.icon;
        const people = grouped[tier.role];
        return (
          <Card key={tier.role}>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <span
                  className={`inline-flex h-7 w-7 items-center justify-center rounded-full ${tier.tint}`}
                >
                  <Icon className="h-4 w-4" aria-hidden />
                </span>
                {tier.label}
                <Badge variant="secondary" className="ml-auto">
                  {people.length}
                </Badge>
              </CardTitle>
              <CardDescription>{tier.blurb}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-2">
              {people.length === 0 ? (
                <p className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Users className="h-4 w-4" aria-hidden />
                  No {tier.label.toLowerCase()} yet.
                </p>
              ) : (
                people.map((m) => (
                  <div
                    key={m.user_id}
                    className="flex flex-col rounded-lg border border-border/60 px-3 py-2"
                  >
                    <span className="text-sm font-medium text-foreground">
                      {m.name || "—"}
                    </span>
                    <span className="text-xs text-muted-foreground">
                      {m.email || m.user_id}
                    </span>
                  </div>
                ))
              )}
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}
